import React, { useState, useRef } from "react";
import { Flex, Text, Button, useToast, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay } from "@chakra-ui/react";
import { tokenUtils } from "../../controller/api";
import { useNavigate } from "react-router-dom";
import { requestPermission } from "../../firebase";

const Header = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // 알림창 상태 관리
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const cancelRef = useRef();

  // 로그아웃 대화상자 열기
  const openLogoutAlert = () => {
    setIsAlertOpen(true);
  };

  // 로그아웃 대화상자 닫기
  const closeLogoutAlert = () => {
    setIsAlertOpen(false);
  };

  // 실제 로그아웃 처리 함수
  const handleLogout = () => {
    const result = tokenUtils.clearTokens();

    if (result) {
      console.log("로그아웃 성공");
      toast({
        title: "로그아웃 되었습니다.",
        status: "info",
        duration: 200,
        isClosable: true,
        position: "bottom",
      });
      navigate("/", { replace: true });
    }

    // 대화상자 닫기
    closeLogoutAlert();
  };

  const requestNotificationPermission = async () => {
    console.log("🔔 헤더에서 알림 권한 요청 버튼 클릭됨");

    if (!("Notification" in window)) {
      alert("⚠️ 이 브라우저는 알림을 지원하지 않습니다.");
      return;
    }

    // PWA 환경 감지
    const isPWA = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true || document.referrer.includes("android-app://");

    console.log("📱 PWA 환경:", isPWA);

    // PWA에서는 더 친화적인 안내 제공
    if (isPWA) {
      const currentPermission = Notification.permission;
      console.log("📋 현재 권한 상태:", currentPermission);

      if (currentPermission === "denied") {
        alert(
          "🚫 알림이 차단되어 있습니다.\n\n브라우저 설정에서 직접 허용해주세요:\n\n📱 Android Chrome:\n1. 주소창 옆 🔒 아이콘 터치\n2. 알림 > 허용\n\n🍎 iOS Safari:\n1. 설정 > Safari > 웹사이트 > 알림\n2. 이 사이트 > 허용"
        );
        return;
      }

      if (currentPermission === "granted") {
        alert("✅ 알림이 이미 허용되어 있습니다!");
        return;
      }

      // PWA에서 팝업이 먹통일 경우를 위한 특별 안내
      alert(
        "⚠️ PWA 환경에서 알림 권한 팝업이 응답하지 않을 수 있습니다.\n\n만약 팝업이 클릭되지 않으면:\n\n📱 수동 설정 방법:\n1. 브라우저 메뉴 (⋮) > 설정\n2. 사이트 설정 > 알림\n3. 배송쿡 사이트 찾아서 '허용'으로 변경\n\n지금 권한 요청을 시도합니다..."
      );
    }

    try {
      console.log("🎯 Firebase 권한 요청 함수 호출");

      // 로딩 표시를 위한 임시 알림
      const loadingToast = setTimeout(() => {
        console.log("⏰ 권한 요청이 5초 이상 걸리고 있습니다...");
      }, 5000);

      const token = await requestPermission();
      clearTimeout(loadingToast);

      if (token) {
        const shortToken = token.substring(0, 20) + "...";
        alert(`🎉 알림 허용 완료!\n\n토큰: ${shortToken}\n\n이제 푸시 알림을 받을 수 있습니다.`);
        console.log("✅ 알림 설정 완료, 토큰:", shortToken);
      } else {
        // 실패했을 때 수동 설정 안내
        alert(
          `❌ 알림 권한 설정에 실패했습니다.\n\n🔧 수동 설정 방법:\n\n📱 Android:\n1. Chrome 메뉴 > 설정 > 사이트 설정\n2. 알림 > 허용된 사이트에 추가\n\n🍎 iOS:\n1. Safari 설정 > 웹사이트 > 알림\n2. 배송쿡 사이트 허용\n\n또는 앱을 다시 설치해보세요.`
        );
        console.log("❌ 알림 설정 실패");
      }
    } catch (err) {
      console.error("💥 권한 요청 중 오류 발생:", err);
      alert(`⚠️ 알림 설정 중 오류가 발생했습니다.\n\n오류: ${err.message}\n\n🔧 해결 방법:\n1. 앱을 완전히 종료 후 다시 실행\n2. 브라우저 설정에서 수동으로 알림 허용\n3. PWA 앱 재설치`);
    }
  };

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" p={4}>
        <Text fontSize="lg">배송 현황</Text>
        <Flex gap={3} alignItems="center" justifyContent="center">
          <Button size="sm" colorScheme="blue" variant="outline" onClick={requestNotificationPermission} px={3}>
            🔔 알림설정
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="teal"
            color="red"
            px={3}
            onClick={openLogoutAlert} // 대화상자 열기로 변경
          >
            로그아웃
          </Button>
        </Flex>
      </Flex>

      {/* 로그아웃 확인 대화상자 */}
      <AlertDialog isOpen={isAlertOpen} leastDestructiveRef={cancelRef} onClose={closeLogoutAlert} isCentered size="xs">
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              로그아웃
            </AlertDialogHeader>

            <AlertDialogBody>정말로 로그아웃 하시겠습니까?</AlertDialogBody>

            <AlertDialogFooter>
              <Button colorScheme="red" onClick={handleLogout}>
                네
              </Button>
              <Button ref={cancelRef} onClick={closeLogoutAlert} ml={3}>
                아니오
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default Header;
